import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';

export interface CanvasSize {
  width: number;
  height: number;
  nameKey?: string;
}

export const SIZE_PRESETS: CanvasSize[] = [
  { width: 1000, height: 1000, nameKey: 'sizeSquare' },
  { width: 900, height: 1100 },
  { width: 1080, height: 1080, nameKey: 'sizeInstagram' },
  { width: 1920, height: 1080, nameKey: 'sizeFullHD' },
  { width: 1080, height: 1920, nameKey: 'sizeStory' },
  { width: 1024, height: 1536 },
  { width: 1200, height: 630, nameKey: 'sizeFacebook' },
  { width: 1500, height: 500, nameKey: 'sizeTwitter' },
  { width: 2560, height: 1600, nameKey: 'size2K' },
  { width: 1024, height: 768 },
  { width: 800, height: 600 },
  { width: 2480, height: 3508, nameKey: 'A4' },
  { width: 1748, height: 2480, nameKey: 'A5' },
];

interface SizeSelectorProps {
  width: number;
  height: number;
  onSizeChange: (width: number, height: number) => void;
}

const SizeSelector: React.FC<SizeSelectorProps> = ({
  width,
  height,
  onSizeChange,
}) => {
  const { t } = useLanguage();
  
  const handlePresetChange = (value: string) => {
    const preset = SIZE_PRESETS.find(p => `${p.width}x${p.height}` === value);
    if (preset) {
      onSizeChange(preset.width, preset.height);
    }
  };

  const currentPreset = SIZE_PRESETS.find(p => p.width === width && p.height === height);

  const getPresetName = (preset: CanvasSize) => {
    const dimensions = `${preset.width}×${preset.height}`;
    if (preset.nameKey) {
      const translated = (t as any)[preset.nameKey];
      if (translated) {
        return `${dimensions} (${translated})`;
      }
      return `${dimensions} (${preset.nameKey})`;
    }
    return dimensions;
  };

  return (
    <div className="space-y-3">
      <h2 className="panel-title font-semibold text-foreground">{t.canvasSize}</h2>
      
      <Select
        value={currentPreset ? `${currentPreset.width}x${currentPreset.height}` : 'custom'}
        onValueChange={handlePresetChange}
      >
        <SelectTrigger className="w-full h-9 text-sm" aria-label="Select canvas size">
          <SelectValue placeholder={t.selectSize} />
        </SelectTrigger>
        <SelectContent>
          {SIZE_PRESETS.map((preset) => (
            <SelectItem key={`${preset.width}x${preset.height}`} value={`${preset.width}x${preset.height}`}>
              {getPresetName(preset)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Label htmlFor="canvas-width" className="panel-label text-muted-foreground">{t.width}</Label>
          <Input
            id="canvas-width"
            type="number"
            value={width}
            onChange={(e) => onSizeChange(Number(e.target.value) || 100, height)}
            className="h-8 text-sm"
            min={100}
            max={4000}
          />
        </div>
        <span className="text-muted-foreground mt-5" aria-hidden="true">×</span>
        <div className="flex-1">
          <Label htmlFor="canvas-height" className="panel-label text-muted-foreground">{t.height}</Label>
          <Input
            id="canvas-height"
            type="number"
            value={height}
            onChange={(e) => onSizeChange(width, Number(e.target.value) || 100)}
            className="h-8 text-sm"
            min={100}
            max={4000}
          />
        </div>
      </div>
    </div>
  );
};

export default SizeSelector;